import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, deleteField, serverTimestamp, getDoc } from 'firebase/firestore';
import { appConfig } from '../../config/appConfig';
import NavBar from '../NavBar/NavBar';
import { COUNTRY_CALLING_CODES, normalizeInternationalPhone } from '../../utils/countryCallingCodes';
import './Dashboard.css';

const normalizeEmail = (value) => (value || '').toString().trim().toLowerCase();

const isAdministrativeRecord = (usuario, adminIds, adminEmails) => {
  const role = (usuario.role || usuario.rol || usuario.tipoUsuario || '').toString().trim().toLowerCase();
  const department = (usuario.departamento || '').toString().trim().toUpperCase();
  const email = normalizeEmail(usuario.correo || usuario.email);
  const hasCustomerIdentity = [
    usuario.nombre_cliente,
    usuario.cedula,
    usuario.telefono_cliente,
    usuario.CC,
    usuario.cc_depart
  ].some(value => (value || '').toString().trim());

  return adminIds.has(usuario.id) ||
    (email && adminEmails.has(email)) ||
    usuario.admin === true ||
    usuario.isAdmin === true ||
    ['admin', 'superadmin', 'administrador'].includes(role) ||
    department === 'ADMIN' ||
    !hasCustomerIdentity;
};

function Dashboard() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // Modificar el estado inicial del formulario para incluir CC
  const [reservaForm, setReservaForm] = useState({
    origen: '',
    destino: '',
    comentarios: '',
    fecha: '',
    hora: '',
    cc: '' // Añadir campo CC
  });
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Estados adicionales para la edición y eliminación
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editForm, setEditForm] = useState({
    nombre: '',
    telefono: '',
    codigo_pais: '+593',
    correo: '', // Added field
    cc_depart: '', // Added field
    cedula: '', // Added field
    departamento: '',
    division: '',
    area: '',
    direccion: '',
    code_direccion: '',
    ruta: '',
    status: '1'
  });
  const [updatingUser, setUpdatingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  // Mantener el estado para el departamento pero sin mostrarlo en UI
  const [userDepartment, setUserDepartment] = useState('');
  // Add new state for admin status
  const [isAdmin, setIsAdmin] = useState(false);

  // Nuevo estado para el modal de añadir usuario
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    nombre_cliente: '',
    cc_depart: '', // Reemplaza CC
    cedula: '',
    telefono_cliente: '',
    codigo_pais: '+593',
    correo: '', // Añadido para consistencia con la estructura de datos
    departamento: '',
    division: '',
    area: '',
    direccion_cliente: '',
    code_direccion: '',
    ruta: '',
    status_cliente: '1'
  });
  const [addingUser, setAddingUser] = useState(false);
  const [addUserSuccess, setAddUserSuccess] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchUsuarios = async () => {
      try {
        setLoading(true);

        // Los administradores se gestionan por separado y nunca deben
        // mostrarse como clientes ni estar disponibles para crear reservas.
        let adminIds = new Set();
        let adminEmails = new Set();
        try {
          const adminsSnapshot = await getDocs(collection(db, 'levapan_admins'));
          adminIds = new Set(adminsSnapshot.docs.map(adminDoc => adminDoc.id));
          adminEmails = new Set(
            adminsSnapshot.docs
              .map(adminDoc => normalizeEmail(adminDoc.data().email || adminDoc.data().correo))
              .filter(Boolean)
          );
        } catch (adminError) {
          console.warn('No se pudo consultar levapan_admins; se aplicará el filtro por roles.', adminError);
        }
        
        // Primero, obtener el departamento del usuario actual
        const userDocRef = doc(db, 'levapan_usuarios', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        let userDept = '';
        let userIsAdmin = adminIds.has(user.uid) || adminEmails.has(normalizeEmail(user.email));
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          userDept = userData.departamento || '';
          setUserDepartment(userDept);
          
          userIsAdmin = userIsAdmin || isAdministrativeRecord(
            { id: user.uid, ...userData },
            adminIds,
            adminEmails
          );
        }
        setIsAdmin(userIsAdmin);
        console.log("User department:", userDept, "Is Admin:", userIsAdmin);
        
        // Luego obtener todos los usuarios
        const usuariosCollection = collection(db, 'levapan_usuarios');
        const usuariosSnapshot = await getDocs(usuariosCollection);
        let usuariosList = usuariosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter(usuario => !isAdministrativeRecord(usuario, adminIds, adminEmails));
        
        // Filtrar usuarios por departamento SOLO si el usuario NO es admin
        if (userDept && !userIsAdmin) {
          console.log("Filtering by department:", userDept);
          usuariosList = usuariosList.filter(usuario => 
            usuario.departamento === userDept || usuario.id === user.uid
          );
        } else if (userIsAdmin) {
          console.log("Admin user - showing ALL users");
        }
        
        setUsuarios(usuariosList);
      } catch (err) {
        console.error("Error al obtener usuarios:", err);
        setError("Ocurrió un error al cargar los datos. Intenta nuevamente.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsuarios();
  }, [user, navigate]);
  
  // Función para filtrar y buscar usuarios - actualizada sin filtro de estado
  const usuariosFiltrados = usuarios.filter(usuario => {
    // Buscar por término en diferentes campos
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (usuario.nombre_cliente && usuario.nombre_cliente.toLowerCase().includes(term)) ||
        (usuario.CC && usuario.CC.toLowerCase().includes(term)) ||
        (usuario.telefono_cliente && usuario.telefono_cliente.toLowerCase().includes(term))
      );
    }
    
    return true;
  });

  // Función para abrir el modal de reserva
  const openReservaModal = (usuario) => {
    setSelectedUser(usuario);
    
    // Establecer fecha y hora predeterminadas (ahora + 30 minutos)
    const now = new Date();
    const thirtyMinutesLater = new Date(now.getTime() + 30 * 60000);
    
    // Get day, month, and year parts
    const day = thirtyMinutesLater.getDate().toString().padStart(2, '0');
    // JavaScript months are 0-indexed, so add 1
    const month = (thirtyMinutesLater.getMonth() + 1).toString().padStart(2, '0'); 
    const year = thirtyMinutesLater.getFullYear();

    // Formatear fecha en formato DD-MM-YYYY
    // Note: The standard value format for <input type="date"> is YYYY-MM-DD. 
    // Using DD-MM-YYYY might not set the default value correctly in all browsers.
    const formattedDate = `${day}-${month}-${year}`;
    
    // Formatear hora para el input type="time" (HH:MM)
    const hours = thirtyMinutesLater.getHours().toString().padStart(2, '0');
    const minutes = thirtyMinutesLater.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;
    
    // Inicializar el formulario con los datos actuales del usuario
    // Ahora precargamos la dirección del usuario como origen por defecto
    setReservaForm({
      origen: usuario.direccion_cliente || '', // Precargar la dirección del usuario como origen
      destino: '',
      comentarios: '',
      fecha: formattedDate,
      hora: formattedTime,
      cc: usuario.cc_depart || '' // Pre-cargar el CC Departamental del usuario
    });
    
    setModalOpen(true);
    setSuccessMessage('');
  };

  // Función para cerrar el modal
  const closeModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReservaForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para enviar la reserva
  const handleSubmitReserva = async (e) => {
    e.preventDefault();

    if (!reservaForm.origen || !reservaForm.destino || !reservaForm.fecha || !reservaForm.hora) {
      alert("Por favor completa todos los campos obligatorios");
      return;
    }

    if (!selectedUser) {
      alert("Error: No hay usuario seleccionado.");
      return;
    }

    try {
      setSubmitting(true);

      // ---> START: Adjust Date/Time Formatting <---
      let fechaParaGuardar = reservaForm.fecha; // Default to current value

      // Check if the date from the form is YYYY-MM-DD and convert to DD-MM-YYYY
      if (reservaForm.fecha && /^\d{4}-\d{2}-\d{2}$/.test(reservaForm.fecha)) {
        try {
          const parts = reservaForm.fecha.split('-');
          fechaParaGuardar = `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert YYYY-MM-DD to DD-MM-YYYY
        } catch (parseError) {
          console.error("Error converting date format:", parseError);
          // Keep original format if conversion fails
          fechaParaGuardar = reservaForm.fecha; 
        }
      } 
      // Ensure it's DD-MM-YYYY if it wasn't YYYY-MM-DD (handles initial state format)
      else if (reservaForm.fecha && !/^\d{2}-\d{2}-\d{4}$/.test(reservaForm.fecha)) {
         // If it's neither YYYY-MM-DD nor DD-MM-YYYY, log a warning or handle as needed
         console.warn("Fecha format is unexpected:", reservaForm.fecha);
         // Decide how to handle unexpected formats, maybe default or keep original
         // fechaParaGuardar = 'INVALID_FORMAT'; // Example
      }

      // Get current user name and phone
      let nombreSolicitante = '';
      let telefonoSolicitante = '';
      
      // Try to get current user data for solicitante info
      try {
        if (auth.currentUser) {
          const userDocRef = doc(db, 'levapan_usuarios', auth.currentUser.uid);
          const userSnapshot = await getDoc(userDocRef);
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            nombreSolicitante = userData.nombre_cliente || auth.currentUser.displayName || '';
            telefonoSolicitante = userData.telefono_cliente || '';
          } else {
            // Fallback to auth display name if document doesn't exist
            nombreSolicitante = auth.currentUser.displayName || '';
          }
        }
      } catch (userDataError) {
        console.error("Error getting solicitante data:", userDataError);
        // Continue with reservation anyway
      }

      const reservaData = {
        usuario_id: selectedUser.id,
        nombre_cliente: selectedUser.nombre_cliente || 'N/A',
        cc: reservaForm.cc, // Usar el CC del formulario (editable)
        cedula: selectedUser.cedula || '', 
        telefono: selectedUser.telefono_cliente || '', 
        departamento: selectedUser.departamento || '', 
        direcci: selectedUser.direccion_cliente || '', // Guardamos la dirección original del usuario
        direccion_origen: reservaForm.origen.toUpperCase(),
        direccion: reservaForm.origen.toUpperCase(),
        code_direccion: selectedUser.code_direccion || '', 
        status_cliente: selectedUser.status_cliente || '1',
        area: selectedUser.Area || selectedUser.area || '', // Añadir campo Area
        // --- Datos del formulario ---
        origen: reservaForm.origen.toUpperCase(),
        destino: reservaForm.destino.toUpperCase(),
        comentarioConductor: reservaForm.comentarios || '',
        // Store fechaHora using the original form value (might be DD-MM-YYYY or YYYY-MM-DD)
        // Consider standardizing fechaHora to ISO 8601 if possible elsewhere
        fechaHora: fechaParaGuardar, 
        // Store fecha explicitly as DD-MM-YYYY
        fecha: fechaParaGuardar, 
        hora_reserva: reservaForm.hora, // Keep time as HH:MM
        tiempoInicioTaximetro: reservaForm.hora, // Keep time as HH:MM
        // --- Metadatos ---
        status: 'pending',
        createdAt: serverTimestamp(),
        userEmail: auth.currentUser ? auth.currentUser.email : 'usuario_anonimo',
        userId: auth.currentUser ? auth.currentUser.uid : 'anonimo', 
        empresa: appConfig.companyCode,
        // --- Información del solicitante ---
        nombreSolicitante: nombreSolicitante,
        telefonoSolicitante: telefonoSolicitante,
        // Also create solicitante object with complete info
        solicitante: {
          id: auth.currentUser ? auth.currentUser.uid : 'anonimo',
          nombre: nombreSolicitante,
          telefono: telefonoSolicitante,
          email: auth.currentUser ? auth.currentUser.email : 'usuario_anonimo'
        }
      };
      // ---> END: Adjust Date/Time Formatting <---

      console.log("Guardando en reservations:", reservaData); // Log para depuración

      // Guardar en Firestore
      const reservationsRef = collection(db, 'levapan_reservations');
      await addDoc(reservationsRef, reservaData);

      // Mostrar mensaje de éxito
      setSuccessMessage('¡Reserva solicitada con éxito! En breve el conductor se pondrá en contacto.');

      // Reiniciar formulario después de 3 segundos
      setTimeout(() => {
        if (selectedUser) { // Verificar que el modal sigue abierto
          // Resetear solo campos del formulario, mantener CC pre-cargado?
          setReservaForm(prev => ({
            ...prev, // Mantener CC, fecha, hora
            origen: '',
            destino: '',
            comentarios: ''
          }));
          // O resetear todo si prefieres:
          // setReservaForm({ origen: '', destino: '', comentarios: '', fecha: reservaForm.fecha, hora: reservaForm.hora, cc: selectedUser.CC || '' });
        }
        // No cerrar modal automáticamente, permitir al usuario cerrarlo
        // closeModal();
      }, 3000); // Aumentar tiempo para que el mensaje sea visible

    } catch (error) {
      console.error("Error al crear la reserva:", error);
      alert("Ocurrió un error al procesar tu reserva. Intenta nuevamente.");
      setSuccessMessage(''); // Limpiar mensaje en caso de error
    } finally {
      setSubmitting(false);
    }
  };

  // Función para comprobar si un campo debe ocultarse - modificada para mostrar GSC también
  const shouldHideField = (fieldName, value) => {
    if (!value) return true;
    if (fieldName === 'ruta_fija' && value === 'SIN DEFINIR') return true;
    // Mostramos siempre las divisiones y áreas GSC
    return false;
  };

  // Función mejorada para abrir el modal de edición con efectos visuales
  const openEditModal = (usuario) => {
    // Debug the user data
    console.log("Usuario datos para editar:", usuario);
    
    setEditingUser(usuario);
    setEditForm({
      nombre: usuario.nombre_cliente || '',
      telefono: usuario.telefono_cliente || '',
      codigo_pais: usuario.codigo_pais || usuario.codigo_area || usuario.phoneCountryCode || '+593',
      correo: usuario.correo || '', // Populate correo
      cc_depart: usuario.cc_depart || usuario.CC || '', // Compatibilidad con documentos existentes
      cedula: usuario.cedula || '', // Populate cedula
      departamento: usuario.departamento || '',
      division: usuario.division || usuario.Division || '',
      area: usuario.area || usuario.Area || '',
      direccion: usuario.localidad || usuario.direccion_cliente || '',
      code_direccion: usuario.code_direccion || '',
      ruta: usuario.ruta || '',
      status: usuario.status_cliente || '1'
    });
    
    // Añadir una pequeña animación antes de abrir el modal
    setTimeout(() => {
      setEditModalOpen(true);
    }, 100);
  };

  // Función para cerrar el modal de edición
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
  };

  // Función para abrir el modal de confirmación de eliminación
  const openDeleteModal = (usuario) => {
    setUserToDelete(usuario);
    setDeleteModalOpen(true);
  };

  // Función para cerrar el modal de confirmación de eliminación
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  // Función para cerrar el modal de agregar usuario
  const closeAddUserModal = () => {
    setAddUserModalOpen(false);
  };

  // Manejar cambios en el formulario de nuevo usuario
  const handleNewUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUserForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Sincronizar campos Area y area para mantener consistencia
    // Esta lógica ya no es necesaria si Area/area se eliminan del newUserForm
    // if (name === 'Area') {
    //   setNewUserForm(prev => ({
    //     ...prev,
    //     area: value
    //   }));
    // } else if (name === 'area') {
    //   setNewUserForm(prev => ({
    //     ...prev,
    //     Area: value
    //   }));
    // }
  };

  // Función para agregar un nuevo usuario
  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!newUserForm.nombre_cliente) {
      alert("Nombres es un campo obligatorio");
      return;
    }

    try {
      setAddingUser(true);
      
      // Crear objeto con datos del nuevo usuario
      const newUserData = {
        nombre_cliente: newUserForm.nombre_cliente,
        cc_depart: newUserForm.cc_depart, // Usar cc_depart
        cecos: newUserForm.cc_depart,
        cedula: newUserForm.cedula || '',
        telefono_cliente: newUserForm.telefono_cliente || '',
        codigo_pais: newUserForm.codigo_pais || '+593',
        codigo_area: newUserForm.codigo_pais || '+593',
        phoneCountryCode: newUserForm.codigo_pais || '+593',
        telefono_internacional: normalizeInternationalPhone(
          newUserForm.codigo_pais,
          newUserForm.telefono_cliente
        ),
        correo: newUserForm.correo || '', // Añadido
        empresa: appConfig.companyCode,
        departamento: newUserForm.departamento || '',
        division: newUserForm.division || '',
        area: newUserForm.area || '',
        direccion_cliente: newUserForm.direccion_cliente || '',
        localidad: newUserForm.direccion_cliente || '',
        code_direccion: newUserForm.code_direccion || '',
        ruta: newUserForm.ruta || '',
        status_cliente: newUserForm.status_cliente || '1',
        created_at: serverTimestamp(),
        actualizado_en: serverTimestamp()
      };

      // Guardar en Firestore
      const usuariosCollection = collection(db, 'levapan_usuarios');
      const docRef = await addDoc(usuariosCollection, newUserData);
      
      // Actualizar la lista de usuarios con el nuevo usuario
      const newUser = {
        id: docRef.id,
        ...newUserData
      };
      
      setUsuarios([...usuarios, newUser]);
      
      // Mostrar mensaje de éxito
      setAddUserSuccess('Usuario agregado correctamente');
      
      // Resetear el formulario después de 3 segundos
      setTimeout(() => {
        setNewUserForm({
          nombre_cliente: '',
          cc_depart: '', // Actualizado
          cedula: '',
          telefono_cliente: '',
          codigo_pais: '+593',
          correo: '', // Actualizado
          departamento: '',
          division: '',
          area: '',
          direccion_cliente: '',
          code_direccion: '',
          ruta: '',
          status_cliente: '1'
        });
      }, 3000);
      
    } catch (error) {
      console.error("Error al agregar usuario:", error);
      alert(`Error al agregar el usuario: ${error.message || 'Intenta nuevamente.'}`);
    } finally {
      setAddingUser(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Manejar cambios en el formulario de edición
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para enviar la actualización del usuario
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editForm.nombre) { // Updated validation
      alert("Nombre es un campo obligatorio");
      return;
    }
    
    try {
      setUpdatingUser(true);
      
      // Verificar primero si el documento existe
      const userRef = doc(db, 'levapan_usuarios', editingUser.id);
      const docSnapshot = await getDoc(userRef);
      
      if (!docSnapshot.exists()) {
        throw new Error(`Usuario con ID ${editingUser.id} no existe en la base de datos.`);
      }
      
      // Crear objeto con datos actualizados
      const userData = {
        nombre_cliente: editForm.nombre,
        telefono_cliente: editForm.telefono,
        codigo_pais: editForm.codigo_pais || '+593',
        codigo_area: editForm.codigo_pais || '+593',
        phoneCountryCode: editForm.codigo_pais || '+593',
        telefono_internacional: normalizeInternationalPhone(
          editForm.codigo_pais,
          editForm.telefono
        ),
        correo: editForm.correo, 
        cc_depart: editForm.cc_depart, 
        cecos: editForm.cc_depart,
        CC: deleteField(),
        cc: deleteField(),
        cedula: editForm.cedula, 
        departamento: editForm.departamento,
        division: editForm.division,
        area: editForm.area,
        direccion_cliente: editForm.direccion,
        localidad: editForm.direccion,
        code_direccion: editForm.code_direccion,
        ruta: editForm.ruta,
        ruta_fija: deleteField(),
        status_cliente: editForm.status,
        actualizado_en: serverTimestamp()
      };
      
      // Actualizar en Firestore (solo si el documento existe)
      await updateDoc(userRef, userData);
      
      // Actualizar la lista de usuarios
      const updatedUsuarios = usuarios.map(u => {
        if (u.id === editingUser.id) {
          return { ...u, ...userData };
        }
        return u;
      });
      
      setUsuarios(updatedUsuarios);
      closeEditModal();
      
      // Mostrar notificación de éxito
      alert("Usuario actualizado correctamente");
      
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      
      // Mensaje de error más descriptivo según el tipo de error
      if (error.message && error.message.includes("no existe")) {
        alert(`Error: ${error.message} Puede que haya sido eliminado por otro usuario.`);
      } else if (error.code === 'permission-denied') {
        alert("Error: No tienes permisos para actualizar este usuario.");
      } else {
        alert(`Error al actualizar el usuario: ${error.message || 'Intenta nuevamente.'}`);
      }
    } finally {
      setUpdatingUser(false);
    }
  };

  // Función para eliminar un usuario
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setDeletingUser(true);
      
      // Verificar primero si el documento existe antes de eliminar
      const userRef = doc(db, 'levapan_usuarios', userToDelete.id);
      const docSnapshot = await getDoc(userRef);
      
      if (!docSnapshot.exists()) {
        throw new Error(`Usuario con ID ${userToDelete.id} no existe en la base de datos.`);
      }
      
      // Eliminar en Firestore
      await deleteDoc(userRef);
      
      // Actualizar la lista de usuarios
      const updatedUsuarios = usuarios.filter(u => u.id !== userToDelete.id);
      setUsuarios(updatedUsuarios);
      
      closeDeleteModal();
      
      // Mostrar notificación de éxito
      alert("Usuario eliminado correctamente");
      
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      
      // Mensaje de error más descriptivo según el tipo de error
      if (error.message && error.message.includes("no existe")) {
        alert(`Error: ${error.message} Puede que haya sido eliminado por otro usuario.`);
      } else {
        alert(`Error al eliminar el usuario: ${error.message || 'Intenta nuevamente.'}`);
      }
    } finally {
      setDeletingUser(false);
    }
  };

  return (
    <div className="dashboard-container">
      <NavBar />
      <div className="dashboard-content">
        
        
        {/* Display department or admin status */}
        <div className="department-indicator" style={{
          backgroundColor: isAdmin ? '#4CAF50' : '#f5f5f5',
          color: isAdmin ? 'white' : '#333',
          padding: '8px 15px',
          borderRadius: '4px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between' // Alinear elementos a los extremos
        }}>
          <div>
            {isAdmin ? (
              <>
                <i className="fas fa-database" style={{marginRight: '8px'}}></i>
                <strong>ACCESO COMPLETO:</strong>&nbsp;Mostrando usuarios de todos los departamentos
              </>
            ) : (
              <>
                <i className="fas fa-filter" style={{marginRight: '8px'}}></i>
                <strong>Departamento:</strong>&nbsp;{userDepartment || 'No asignado'} - Mostrando solo usuarios de este departamento
              </>
            )}
          </div>
          
          <button 
            className="add-user-btn"
            onClick={() => navigate('/agregar-usuario')}
            style={{
              backgroundColor: '#176046',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '9px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
              boxShadow: '0 2px 7px rgba(0,0,0,0.18)'
            }}
          >
            <i className="fas fa-user-plus" style={{marginRight: '8px'}}></i>
            Agregar Usuario
          </button>
        </div>
        
        {/* Búsqueda - Add search input field */}
        <div className="search-container" style={{
          marginBottom: '20px',
          position: 'relative',
          maxWidth: '600px'
        }}>
          <div className="search-input-wrapper" style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '15px',
              color: '#999'
            }}></i>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por nombre, CC o teléfono..."
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '12px 40px 12px 45px',
                borderRadius: '25px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={clearSearch}
                style={{
                  position: 'absolute',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  color: '#999',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                &times;
              </button>
            )}
          </div>
        </div>
     
        {loading ? (
          <div className="loading-indicator">
            <div className="spinner">
              <div className="double-bounce1"></div>
              <div className="double-bounce2"></div>
            </div>
            <p>Cargando usuarios...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Reintentar</button>
          </div>
        ) : (
          <>
            <div className="usuarios-counter">
              Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
              {isAdmin && (
                <span style={{marginLeft: '10px', fontWeight: 'bold', color: '#4CAF50'}}>
                  (Acceso Total - Modo Admin)
                </span>
              )}
              {searchTerm && (
                <span style={{marginLeft: '10px', fontStyle: 'italic'}}>
                  • Filtrado por: "{searchTerm}"
                </span>
              )}
            </div>
            
            {usuariosFiltrados.length > 0 ? (
              <div className="usuarios-grid">
                {usuariosFiltrados.map(usuario => (
                  <div key={usuario.id} className="usuario-card">
                    <div className={`usuario-card-header ${usuario.status_cliente === "1" ? "" : "inactive"}`}>
                      <h3 className="usuario-nombre">{usuario.nombre_cliente || 'Nombre no disponible'}</h3>
                      <span className={`status-indicator ${usuario.status_cliente === "1" ? "active" : "inactive"}`}>
                        {usuario.status_cliente === "1" ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                    
                    <div className="usuario-card-body">
                      {/* Teléfono */}
                      {!shouldHideField('telefono', usuario.telefono_cliente) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-phone"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">Teléfono</div>
                            <div className="info-value">
                              {usuario.telefono_internacional || `${usuario.codigo_pais || usuario.codigo_area || '+593'} ${usuario.telefono_cliente}`}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Correo Electrónico */}
                      {!shouldHideField('correo', usuario.correo) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-envelope"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">Correo</div>
                            <div className="info-value">{usuario.correo}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* CECOS: usa el campo existente cc_depart, sin duplicarlo */}
                      {!shouldHideField('cc_depart', usuario.cc_depart || usuario.CC) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-id-badge"></i> {/* Icon for CC Departamental */}
                          </div>
                          <div className="info-content">
                            <div className="info-label">CECOS</div>
                            <div className="info-value">{usuario.cc_depart || usuario.CC}</div>
                          </div>
                        </div>
                      )}

                      {/* Cédula */}
                      {!shouldHideField('cedula', usuario.cedula) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-id-card"></i> {/* Icon for Cédula */}
                          </div>
                          <div className="info-content">
                            <div className="info-label">Cédula</div>
                            <div className="info-value">{usuario.cedula}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Departamento */}
                      {!shouldHideField('departamento', usuario.departamento) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-building"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">Departamento</div>
                            <div className="info-value">{usuario.departamento}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* División */}
                      {!shouldHideField('division', usuario.division || usuario.Division) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-layer-group"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">División</div>
                            <div className="info-value">{usuario.division || usuario.Division}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* GSC Área - Nueva sección */}
                      {(!shouldHideField('Area', usuario.Area) || !shouldHideField('area', usuario.area)) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-sitemap"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">Área</div>
                            <div className="info-value">{usuario.Area || usuario.area || ''}</div>
                          </div>
                        </div>
                      )}

                      {/* Localidad: reutiliza direccion_cliente */}
                      {!shouldHideField('localidad', usuario.localidad || usuario.direccion_cliente) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-map-marker-alt"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">Localidad</div>
                            <div className="info-value">{usuario.localidad || usuario.direccion_cliente}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Ruta */}
                      {!shouldHideField('ruta', usuario.ruta) && (
                        <div className="info-row">
                          <div className="info-icon">
                            <i className="fas fa-route"></i>
                          </div>
                          <div className="info-content">
                            <div className="info-label">Ruta</div>
                            <div className="info-value">{usuario.ruta}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="usuario-card-actions">
                      <button 
                        className="card-action-btn reservar"
                        onClick={() => openReservaModal(usuario)}
                        disabled={!isAdmin && usuario.status_cliente !== "1"}
                      >
                        <i className="fas fa-taxi"></i> Reservar
                      </button>
                      <button 
                        className="card-action-btn edit"
                        onClick={() => openEditModal(usuario)}
                      >
                        <i className="fas fa-edit"></i> Editar
                      </button>
                      <button 
                        className="card-action-btn danger"
                        onClick={() => openDeleteModal(usuario)}
                      >
                        <i className="fas fa-trash"></i> Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-results">
                <i className="fas fa-users-slash"></i>
                <h3>No se encontraron usuarios</h3>
                <p>Intenta cambiar los filtros de búsqueda</p>
              </div>
            )}
            
            {/* Modal de Reserva */}
            {modalOpen && selectedUser && (
              <div className="modal-overlay" onClick={(e) => {
                if (e.target.className === 'modal-overlay') closeModal();
              }}>
                <div className="modal-container">
                  <div className="modal-header">
                    <h2>Reservar Taxi</h2>
                    <button className="modal-close" onClick={closeModal}>&times;</button>
                  </div>

                  <div className="modal-body">
                    {successMessage && (
                      <div className="success-message">
                        <i className="fas fa-check-circle"></i>
                        <span>{successMessage}</span>
                      </div>
                    )}

                    {/* ---> INICIO: Mostrar datos básicos del usuario en el modal <--- */}
                    <div className="form-user-info">
                      <div className="user-info-item">
                        <div className="user-info-label">Cliente:</div>
                        <div className="user-info-value">{selectedUser.nombre_cliente || 'N/A'}</div>
                      </div>
                      {/* Campo editable para CC */}
                      <div className="user-info-item cc-edit-container">
                        <div className="user-info-label">CC:</div>
                        <input
                          type="text"
                          className="cc-edit-input"
                          name="cc"
                          value={reservaForm.cc}
                          onChange={handleInputChange}
                          placeholder="Ingrese CC"
                        />
                      </div>
                    </div>
                    {/* ---> FIN: Mostrar datos básicos del usuario en el modal <--- */}

                    <form className="modal-form" onSubmit={handleSubmitReserva}>
                      <div className="form-group">
                        <label htmlFor="origen">Punto de partida *</label>
                        <input
                          type="text"
                          id="origen"
                          name="origen"
                          value={reservaForm.origen}
                          onChange={handleInputChange}
                          placeholder="Ingresa la dirección de origen"
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="destino">Punto de destino *</label>
                        <input
                          type="text"
                          id="destino"
                          name="destino"
                          value={reservaForm.destino}
                          onChange={handleInputChange}
                          placeholder="Ingresa la dirección de destino"
                          required
                        />
                      </div>
                      
                      <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label htmlFor="fecha">Fecha *</label>
                          <input
                            type="date"
                            id="fecha"
                            name="fecha"
                            value={reservaForm.fecha}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        
                        <div className="form-group" style={{ flex: 1 }}>
                          <label htmlFor="hora">Hora *</label>
                          <input
                            type="time"
                            id="hora"
                            name="hora"
                            value={reservaForm.hora}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="comentarios">Comentarios adicionales</label>
                        <textarea
                          id="comentarios"
                          name="comentarios"
                          value={reservaForm.comentarios}
                          onChange={handleInputChange}
                          placeholder="Información adicional sobre el viaje..."
                        />
                      </div>
                      
                      <div className="modal-footer">
                        <button 
                          type="button" 
                          className="modal-btn cancel"
                          onClick={closeModal}
                        >
                          Cancelar
                        </button>
                        <button 
                          type="submit" 
                          className="modal-btn submit"
                          disabled={submitting || !!successMessage}
                        >
                          {submitting ? 'Procesando...' : 'Solicitar Reserva'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
            
            {/* Modal de Edición Mejorado */}
            {editModalOpen && editingUser && (
              <div className="modal-overlay" onClick={(e) => {
                if (e.target.className === 'modal-overlay') closeEditModal();
              }}>
                <div className="modal-container">
                  <div className="modal-header" style={{ backgroundColor: '#2196F3' }}>
                    <h2>Editar Usuario</h2>
                    <button className="modal-close" onClick={closeEditModal}>&times;</button>
                  </div>
                  
                  <div className="modal-body">
                    <h3 className="edit-modal-title">
                      <i className="fas fa-user-edit"></i> Modificar información de usuario
                    </h3>
                    
                    <div className="edit-form-container">
                      <form className="modal-form" onSubmit={handleUpdateUser}>
                        <div className="form-group">
                          <label htmlFor="nombre">Nombres *</label>
                          <input
                            type="text"
                            id="nombre"
                            name="nombre"
                            value={editForm.nombre}
                            onChange={handleEditInputChange}
                            placeholder="Nombres del usuario"
                            required
                          />
                        </div>
                        
                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="cc_depart">CECOS</label>
                            <input
                              type="text"
                              id="cc_depart"
                              name="cc_depart"
                              value={editForm.cc_depart}
                              onChange={handleEditInputChange}
                              placeholder="Código CECOS"
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="cedula">Cédula</label>
                            <input
                              type="text"
                              id="cedula"
                              name="cedula"
                              value={editForm.cedula}
                              onChange={handleEditInputChange}
                              placeholder="Número de cédula"
                            />
                          </div>
                        </div>

                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="telefono">Teléfono</label>
                            <div className="international-phone-field">
                              <select
                                aria-label="Código internacional"
                                name="codigo_pais"
                                value={editForm.codigo_pais}
                                onChange={handleEditInputChange}
                              >
                                {COUNTRY_CALLING_CODES.map(country => (
                                  <option key={`${country.code}-${country.country}`} value={country.code}>
                                    {country.flag} {country.code} · {country.country}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="tel"
                                id="telefono"
                                name="telefono"
                                value={editForm.telefono}
                                onChange={handleEditInputChange}
                                placeholder="Número local"
                                inputMode="tel"
                              />
                            </div>
                            <small className="phone-preview">
                              Internacional: {normalizeInternationalPhone(editForm.codigo_pais, editForm.telefono) || 'sin número'}
                            </small>
                          </div>
                           <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="correo">Correo Electrónico</label>
                            <input
                              type="email"
                              id="correo"
                              name="correo"
                              value={editForm.correo}
                              onChange={handleEditInputChange}
                              placeholder="Correo electrónico"
                            />
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="departamento">Departamento</label>
                          <input
                            type="text"
                            id="departamento"
                            name="departamento"
                            value={editForm.departamento}
                            onChange={handleEditInputChange}
                            placeholder="Departamento"
                          />
                        </div>

                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="division">División</label>
                            <input
                              type="text"
                              id="division"
                              name="division"
                              value={editForm.division}
                              onChange={handleEditInputChange}
                              placeholder="División"
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="area">Área</label>
                            <input
                              type="text"
                              id="area"
                              name="area"
                              value={editForm.area}
                              onChange={handleEditInputChange}
                              placeholder="Área"
                            />
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="direccion">Localidad</label>
                          <input
                            type="text"
                            id="direccion"
                            name="direccion"
                            value={editForm.direccion}
                            onChange={handleEditInputChange}
                            placeholder="Localidad"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="code_direccion">Código de dirección</label>
                          <input
                            type="text"
                            id="code_direccion"
                            name="code_direccion"
                            value={editForm.code_direccion}
                            onChange={handleEditInputChange}
                            placeholder="Código o referencia de la dirección"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="ruta">Ruta</label>
                          <input
                            type="text"
                            id="ruta"
                            name="ruta"
                            value={editForm.ruta}
                            onChange={handleEditInputChange}
                            placeholder="Ruta (puede quedar vacía)"
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="status">Estado</label>
                          <select
                            id="status"
                            name="status"
                            value={editForm.status}
                            onChange={handleEditInputChange}
                          >
                            <option value="1">Activo</option>
                            <option value="0">Inactivo</option>
                          </select>
                        </div>
                        
                        <div className="modal-footer">
                          <button 
                            type="button" 
                            className="modal-btn cancel"
                            onClick={closeEditModal}
                          >
                            Cancelar
                          </button>
                          <button 
                            type="submit" 
                            className="modal-btn submit"
                            style={{ backgroundColor: '#2196F3' }}
                            disabled={updatingUser}
                          >
                            {updatingUser ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i> Guardando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save"></i> Guardar Cambios
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de Confirmación de Eliminación */}
            {deleteModalOpen && userToDelete && (
              <div className="modal-overlay" onClick={(e) => {
                if (e.target.className === 'modal-overlay') closeDeleteModal();
              }}>
                <div className="modal-container delete-confirmation">
                  <div className="modal-header delete">
                    <h2>Confirmar Eliminación</h2>
                    <button className="modal-close" onClick={closeDeleteModal}>&times;</button>
                  </div>
                  
                  <div className="modal-body">
                    <div className="delete-warning">
                      <i className="fas fa-exclamation-triangle"></i>
                      <p>¿Estás seguro de que deseas eliminar este usuario?</p>
                    </div>
                    
                    <div className="delete-user-info">
                      <p><strong>Nombre:</strong> {userToDelete.nombre_cliente}</p>
                      {userToDelete.CC && <p><strong>CC:</strong> {userToDelete.CC}</p>}
                    </div>
                    
                    <p className="delete-note">Esta acción no se puede deshacer.</p>
                    
                    <div className="modal-footer">
                      <button 
                        className="modal-btn cancel"
                        onClick={closeDeleteModal}
                      >
                        Cancelar
                      </button>
                      <button 
                        className="modal-btn delete"
                        onClick={handleDeleteUser}
                        disabled={deletingUser}
                      >
                        {deletingUser ? 'Eliminando...' : 'Eliminar Usuario'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Modal para Agregar Usuario */}
            {addUserModalOpen && (
              <div className="modal-overlay" onClick={(e) => {
                if (e.target.className === 'modal-overlay') closeAddUserModal();
              }}>
                <div className="modal-container" style={{maxWidth: '600px'}}>
                  <div className="modal-header" style={{ backgroundColor: '#4CAF50' }}>
                    <h2><i className="fas fa-user-plus"></i> Agregar Nuevo Usuario</h2>
                    <button className="modal-close" onClick={closeAddUserModal}>&times;</button>
                  </div>
                  
                  <div className="modal-body">
                    {addUserSuccess && (
                      <div className="success-message" style={{
                        backgroundColor: '#e8f5e9',
                        color: '#388e3c',
                        padding: '10px',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <i className="fas fa-check-circle" style={{marginRight: '10px', fontSize: '20px'}}></i>
                        <span>{addUserSuccess}</span>
                      </div>
                    )}
                    
                    <form className="modal-form" onSubmit={handleAddUser}>
                      <div className="form-section">
                        <h3>Información Básica</h3>
                        
                        <div className="form-group">
                          <label htmlFor="nombre_cliente">Nombres *</label>
                          <input
                            type="text"
                            id="nombre_cliente"
                            name="nombre_cliente"
                            value={newUserForm.nombre_cliente}
                            onChange={handleNewUserInputChange}
                            placeholder="Nombres del usuario"
                            required
                          />
                        </div>
                        
                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="cc_depart">CECOS</label> 
                            <input
                              type="text"
                              id="cc_depart" // Actualizado
                              name="cc_depart" // Actualizado
                              value={newUserForm.cc_depart} // Actualizado
                              onChange={handleNewUserInputChange}
                              placeholder="Código CECOS"
                            />
                          </div>
                          
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="cedula">Cédula</label>
                            <input
                              type="text"
                              id="cedula"
                              name="cedula"
                              value={newUserForm.cedula}
                              onChange={handleNewUserInputChange}
                              placeholder="Número de cédula"
                            />
                          </div>
                        </div>
                        
                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="telefono_cliente">Teléfono</label>
                            <div className="international-phone-field">
                              <select
                                aria-label="Código internacional"
                                name="codigo_pais"
                                value={newUserForm.codigo_pais}
                                onChange={handleNewUserInputChange}
                              >
                                {COUNTRY_CALLING_CODES.map(country => (
                                  <option key={`${country.code}-${country.country}`} value={country.code}>
                                    {country.flag} {country.code} · {country.country}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="tel"
                                id="telefono_cliente"
                                name="telefono_cliente"
                                value={newUserForm.telefono_cliente}
                                onChange={handleNewUserInputChange}
                                placeholder="Número local"
                                inputMode="tel"
                              />
                            </div>
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="correo">Correo</label>
                            <input
                              type="email"
                              id="correo"
                              name="correo"
                              value={newUserForm.correo}
                              onChange={handleNewUserInputChange}
                              placeholder="Correo electrónico"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-section">
                        <h3>Información Departamental y Dirección</h3> 
                        
                        <div className="form-group">
                          <label htmlFor="departamento">Departamento</label>
                          <input
                            type="text"
                            id="departamento"
                            name="departamento"
                            value={newUserForm.departamento}
                            onChange={handleNewUserInputChange}
                            placeholder="Departamento"
                          />
                        </div>

                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="division">División</label>
                            <input
                              type="text"
                              id="division"
                              name="division"
                              value={newUserForm.division}
                              onChange={handleNewUserInputChange}
                              placeholder="División"
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="area">Área</label>
                            <input
                              type="text"
                              id="area"
                              name="area"
                              value={newUserForm.area}
                              onChange={handleNewUserInputChange}
                              placeholder="Área"
                            />
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="direccion_cliente">Localidad</label>
                          <input
                            type="text"
                            id="direccion_cliente"
                            name="direccion_cliente"
                            value={newUserForm.direccion_cliente}
                            onChange={handleNewUserInputChange}
                            placeholder="Localidad"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="code_direccion">Código de dirección</label>
                          <input
                            type="text"
                            id="code_direccion"
                            name="code_direccion"
                            value={newUserForm.code_direccion}
                            onChange={handleNewUserInputChange}
                            placeholder="Código o referencia de la dirección"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="ruta">Ruta</label>
                          <input
                            type="text"
                            id="ruta"
                            name="ruta"
                            value={newUserForm.ruta}
                            onChange={handleNewUserInputChange}
                            placeholder="Ruta (puede quedar vacía)"
                          />
                        </div>

                        <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                          
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="status_cliente">Estado</label>
                            <select
                              id="status_cliente"
                              name="status_cliente"
                              value={newUserForm.status_cliente}
                              onChange={handleNewUserInputChange}
                            >
                              <option value="1">Activo</option>
                              <option value="0">Inactivo</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      <div className="modal-footer">
                        <button 
                          type="button" 
                          className="modal-btn cancel"
                          onClick={closeAddUserModal}
                        >
                          Cancelar
                        </button>
                        <button 
                          type="submit" 
                          className="modal-btn submit"
                          style={{ backgroundColor: '#4CAF50' }}
                          disabled={addingUser || addUserSuccess}
                        >
                          {addingUser ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i> Agregando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-user-plus"></i> Agregar Usuario
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {usuariosFiltrados.length > 12 && (
              <div className="pagination">
                <button className="pagination-btn" disabled><i className="fas fa-chevron-left"></i></button>
                <span className="pagination-page active">1</span>
                <span className="pagination-page">2</span>
                <span className="pagination-page">3</span>
                <span className="pagination-dots">...</span>
                <button className="pagination-btn"><i className="fas fa-chevron-right"></i></button>
              </div>
            )}

            {/* Información del creador */}
            <div className="creator-info">
              <h3>Desarrollado por Systemtaxi</h3>
              <p>Sistema de gestión de usuarios y reservas de taxis</p>
              
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
